/**
 * SidebarUsageBadge — iter-120 B4 (updated: AI-credit gauge)
 *
 * Compact plan/credit pill in the sidebar footer. Polls
 * `/billing/usage-summary` every 60s and renders the org's Sense AI
 * credit consumption (used vs plan-included) as a thin horizontal
 * progress bar. Reuses the EXISTING `ai_credits` block from the billing
 * usage-summary response — no new endpoint or calculation.
 *
 *   - green    — <80% of AI credits used
 *   - amber    — 80% ≤ used < 100%
 *   - red      — ≥100% used (credits exhausted)
 *   - neutral  — Super Admin / no org plan → "PLATFORM" pill (no bar)
 *   - unlimited— plan grants unlimited AI credits → "Unlimited" (no bar)
 *
 * Click → /billing to inspect the full breakdown. Icon-only when the
 * sidebar is collapsed.
 *
 * Configurable via env:
 *   REACT_APP_USAGE_POLL_MS (default 60000) — poll cadence in ms.
 *
 * The badge silently no-ops on any fetch failure so a billing outage
 * never breaks the sidebar.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, AlertTriangle, ShieldCheck, Infinity as InfinityIcon } from "lucide-react";
import api from "@/lib/api";


const POLL_MS = Number(process.env.REACT_APP_USAGE_POLL_MS || 60000);


export default function SidebarUsageBadge({ collapsed }) {
    const nav = useNavigate();
    const [summary, setSummary] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        let alive = true;
        const fetchOnce = async () => {
            try {
                const r = await api.get("/billing/usage-summary");
                if (alive) {
                    setSummary(r.data || null);
                    setErr(false);
                }
            } catch {
                if (alive) setErr(true);
            }
        };
        fetchOnce();
        const t = setInterval(fetchOnce, POLL_MS);
        // Refresh immediately when credits are topped up (auto-approval) so the
        // pill reflects the new allowance without waiting for the poll.
        const onCredits = () => fetchOnce();
        window.addEventListener("billing:credits-updated", onCredits);
        return () => { alive = false; clearInterval(t); window.removeEventListener("billing:credits-updated", onCredits); };
    }, []);

    // Resolve AI-credit state from the SAME usage-summary payload the backend
    // enforces. Edge cases handled explicitly:
    //   - platform-only (no plan)      → neutral "PLATFORM" pill, no bar
    //   - unlimited (included null/0)  → "Unlimited", no bar
    //   - included = 0 / no cap        → treated as unlimited
    //   - used = 0                     → 0% (empty bar)
    //   - used >= included             → 100% (clamped), red
    const view = useMemo(() => {
        const plan = summary?.plan_slug;
        const ai = summary?.ai_credits || null;
        const isPlatformOnly = !plan;

        const included = Number(ai?.included) || 0;
        const used = Number(ai?.used) || 0;
        // Backend marks unlimited when included is null/0.
        const unlimited = !!(ai?.unlimited) || (!!ai && included <= 0);
        const hasCap = !!ai && !unlimited && included > 0;
        const pct = hasCap ? Math.max(0, Math.min(100, Math.round((used / included) * 100))) : 0;

        let tier = "neutral";
        if (isPlatformOnly || !ai) tier = "neutral";
        else if (unlimited) tier = "green";
        else if (pct >= 100) tier = "red";
        else if (pct >= 80) tier = "amber";
        else tier = "green";

        return { plan, ai, isPlatformOnly, included, used, unlimited, hasCap, pct, tier };
    }, [summary]);

    // Hard fail / not loaded yet — bail so we don't render a flicker.
    if (err || !summary) return null;

    const { plan, isPlatformOnly, included, used, unlimited, hasCap, pct, tier } = view;

    const tone = {
        neutral: "border-border text-muted-foreground",
        green:   "border-[hsl(var(--success))]/40 text-[hsl(var(--success))]",
        amber:   "border-amber-500/50 text-amber-600",
        red:     "border-destructive/50 text-destructive",
    }[tier];
    const barTone = {
        neutral: "bg-muted-foreground/40",
        green:   "bg-[hsl(var(--success))]",
        amber:   "bg-amber-500",
        red:     "bg-destructive",
    }[tier];
    const Icon = tier === "red"       ? AlertTriangle
               : isPlatformOnly       ? ShieldCheck
               : unlimited            ? InfinityIcon
               :                        Gauge;

    const planLabel = isPlatformOnly ? "PLATFORM" : plan;
    // Right-aligned status text — never the misleading static "0%".
    const rightLabel = isPlatformOnly ? "SA"
                     : unlimited      ? "Unlimited"
                     : hasCap         ? `${pct}%`
                     :                  "";
    // Show the thin bar only when there is a real plan credit cap.
    const showBar = hasCap;

    const titleStr = isPlatformOnly
        ? "Super admin · no org plan applies. Click to open Billing."
        : unlimited
          ? `Plan ${plan} · unlimited AI credits. Click for the full breakdown.`
          : hasCap
            ? `AI credits · ${used.toLocaleString()} / ${included.toLocaleString()} used (${pct}%). Click for the full breakdown.`
            : `Plan ${plan} · click for the full breakdown.`;

    if (collapsed) {
        return (
            <button
                type="button"
                onClick={() => nav("/billing")}
                title={titleStr}
                className={`mx-auto my-2 flex items-center justify-center h-7 w-7 rounded-full border ${tone} bg-transparent hover:bg-secondary/40`}
                data-testid="sidebar-usage-badge"
            >
                <Icon className="h-3.5 w-3.5" />
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={() => nav("/billing")}
            className={`mx-3 my-2 flex flex-col gap-1.5 px-2.5 py-1.5 rounded-sm border ${tone} bg-transparent hover:bg-secondary/30 text-[11px]`}
            data-testid="sidebar-usage-badge"
            title={titleStr}
        >
            <div className="flex items-center gap-2 w-full">
                <Icon className="h-3 w-3 shrink-0" />
                <span className="flex-1 text-left truncate font-mono uppercase tracking-wide">
                    {planLabel}
                </span>
                {rightLabel && (
                    <span className="font-mono shrink-0 text-[10px]" data-testid="sidebar-usage-badge-pct">
                        {rightLabel}
                    </span>
                )}
            </div>
            {showBar && (
                <div
                    className="h-1 w-full rounded-full bg-secondary overflow-hidden"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="AI credits used"
                    data-testid="sidebar-usage-badge-bar"
                >
                    <div
                        className={`h-full rounded-full ${barTone}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </button>
    );
}
